import { useRoleAccess, useTenantVat, useView } from '@hooks';
import { Utils } from '@utils';
import { message } from 'antd';
import { observer } from 'mobx-react';
import React, { useEffect, useMemo, useState } from 'react';
import { IServiceExtraFormProps } from './ServiceExtraForm.props';
import { ServiceExtraPresentation } from './ServiceExtraForm.presentation';
import { ServiceExtraMobile } from './ServiceExtraForm.mobile';
import { ServiceExtraTechnician } from './ServiceExtraForm.technician';

export const ServiceExtraForm: React.FC<IServiceExtraFormProps> = observer(
  ({ index = 0, extra, onRemove, onUpdate, disabled = false, showStock = true, onAddToPurchaseOrder }) => {
    const [price, setPrice] = useState<number>(0);
    const [costPrice, setCostPrice] = useState<number>(0);
    const [margin, setMargin] = useState<number>(0);
    const [quantity, setQuantity] = useState<number>(0);
    const [discount, setDiscount] = useState<number>(0);
    const { isMobile } = useView();
    const { order: access } = useRoleAccess();

    const readOnly = !access.editExtras;

    useEffect(() => {
      setPrice(extra.price ?? 0);
      setCostPrice(extra.costPrice ?? 0);
      setMargin(extra.margin ?? 0);
      setQuantity(extra.quantity ?? 0);
      setDiscount(extra.discount ?? 0);
    }, [extra]);

    const vat = useTenantVat();

    const handlePriceChange = (value: number | null) => {
      const newValue = value !== null ? value : 0;
      setPrice(newValue);
      const newMargin =
        costPrice !== 0 ? Utils.roundToTwoDecimals(((newValue - costPrice) / costPrice) * 100) : 0;
      setMargin(newMargin);
      onUpdate({ price: newValue, margin: newMargin });
    };

    const handleQuantityChange = (value: number | null) => {
      const newValue = value !== null ? value : 0;
      setQuantity(newValue);
      onUpdate({ quantity: newValue });
    };

    const handleDiscountChange = (value: number | null) => {
      const newValue = value !== null ? value : 0;
      setDiscount(newValue);
      onUpdate({ discount: newValue });
    };

    const handleCostPriceChange = (value: number | null) => {
      const newValue = value !== null ? value : 0;
      setCostPrice(newValue);
      const newPrice = Utils.roundToTwoDecimals(newValue * (1 + margin / 100));
      setPrice(newPrice);
      onUpdate({ costPrice: newValue, price: newPrice });
    };

    const handleMarginChange = (value: number | null) => {
      const newValue = value !== null ? value : 0;
      setMargin(newValue);
      const newPrice =
        costPrice !== 0 ? Utils.roundToTwoDecimals(costPrice * (1 + newValue / 100)) : price;
      setPrice(newPrice);
      onUpdate({ margin: newValue, price: newPrice });
    };

    const handleQuantityBlur = () => {
      setQuantity((prev) => Utils.roundToTwoDecimals(prev));
      onUpdate({ quantity: Utils.roundToTwoDecimals(quantity) });
    };

    const handleNameChange = (name: string) => {
      onUpdate({ name });
    };

    const handleNumberChange = (number: string) => {
      onUpdate({ number });
    };

    const handleReferenceChange = (reference: string) => {
      onUpdate({ reference });
    };

    const handleTypeChange = (type: string) => {
      onUpdate({ type });
    };

    const subTotal = useMemo(() => {
      const priceAmount = price * quantity;
      const discountAmount = priceAmount * (discount / 100);
      return Utils.roundToTwoDecimals(priceAmount - discountAmount);
    }, [price, quantity, discount]);

    const vatAmount = useMemo(() => {
      return Utils.roundToTwoDecimals((subTotal * vat) / 100);
    }, [subTotal, vat]);

    const total = useMemo(() => {
      return Utils.roundToTwoDecimals(subTotal + vatAmount);
    }, [subTotal, vatAmount]);

    const sharedProps = {
      index,
      extra,
      disabled,
      showStock,
      showPricing: access.extraPricing,
      showDiscount: access.discount,
      showTotals: access.totals,
      vat,
      price,
      costPrice,
      margin,
      quantity,
      discount,
      subTotal,
      vatAmount,
      total,
      handlePriceChange,
      handleCostPriceChange,
      handleMarginChange,
      handleQuantityChange,
      handleDiscountChange,
      handleQuantityBlur,
      handleNameChange,
      handleNumberChange,
      handleReferenceChange,
      handleTypeChange,
      handleRemove: onRemove,
      onAddToPurchaseOrder: onAddToPurchaseOrder
        ? () => {
            if (!extra.name?.trim()) {
              message.error('Enter a part name first');
              return;
            }
            onAddToPurchaseOrder();
          }
        : undefined,
    };

    if (readOnly) return <ServiceExtraTechnician {...sharedProps} />;
    if (isMobile) return <ServiceExtraMobile {...sharedProps} />;
    return <ServiceExtraPresentation {...sharedProps} />;
  }
);
